import { ScreeningRecord, ScreeningResult } from "../models/Interfaces";
import React from "react";
import { useTranslation } from "react-i18next";
import "./recordCard.css";
import { useNavigate } from "react-router";
import { ErrorModal } from "./modals/ErrorModal";
import { getDateTime } from "../utils/data";

interface ScreeningResultCardProps {
  screening: ScreeningRecord;
  screeningResult?: ScreeningResult;
}

// Virus images
const lead = "/assets/images/Icons_Screening_Lead.png";

export default function ScreeningResultCard({
  screening,
  screeningResult,
}: ScreeningResultCardProps) {
  const [modalIsOpen, setModalIsOpen] = React.useState<boolean>(false);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const emptyCard = (screeningResult?.events ?? []).length < 1;
  const type = emptyCard ? "empty" : screeningResult?.type.toLowerCase();

  const goToEvents = () => {
    if (emptyCard) {
      setModalIsOpen(true);
    } else {
      navigate("../screeningevents", {
        state: {
          screening: screening,
          screeningResult: screeningResult,
          back: true,
          title: `${screening.firstName} ${screening.lastName}`,
        },
      });
    }
  };

  const getBadgeText = () => {
    if (emptyCard) {
      return t("screening.records_status_empty");
    }

    return t("screening.records_status", { context: type });
  };

  return (
    <>
      {modalIsOpen && (
        <ErrorModal
          title={t("screening.records_no_events_header")}
          onCloseModal={() => setModalIsOpen(false)}
        >
          <p style={{ whiteSpace: "pre-wrap" }}>
            {t("screening.records_no_events_message")}
            <a
              href="https://nj.gov/health/childhood-lead"
              target="_blank"
              className="docket-link has-text-link"
            >
              nj.gov/health/childhood-lead
            </a>
            .
          </p>
        </ErrorModal>
      )}
      <div
        onClick={() => goToEvents()}
        className="flex max-md:p-1.5 record-card mobile-card sm:h-[5em] md:h-[7em]"
      >
        <div className={`${emptyCard ? "noRecordsStatus" : "recordStatus"} record-badge-bg`}></div>
        <div
          className={
            `record-badge text-xs ml-1 font-semibold text-white` +
            (emptyCard ? " dark:text-dark" : "")
          }
        >
          {getBadgeText()}
        </div>

        <div className="flex flex-col gap-0.5 ml-12 my-auto md:p-3">
          <div
            role="paragraph"
            className="card-title text-dark text-sm font-bold"
            data-testid="record-card-title"
          >
            {t("screening.lead_screening_test")}
          </div>
          {emptyCard ? (
            <p className="text-sm">&nbsp;</p>
          ) : (
            <div role="paragraph" className="flex gap-0.5" data-testid="record-card-description">
              <p className="font-bold text-sm">{t("screening.records_tested")}</p>
              <p className="text-sm">
                {screeningResult?.latestTestDate
                  ? getDateTime(screeningResult.latestTestDate).toLocaleString()
                  : ""}
              </p>
            </div>
          )}
          <div
            role="paragraph"
            className={`text-xs font-bold record-card-description ${emptyCard && "mt-1"}`}
            data-testid="record-card-description"
          >
            {screeningResult?.events?.length || "No"} {t("screening.records_test")}
            {(screeningResult?.events?.length || 0) !== 1 ? `s` : ""}
          </div>
        </div>

        <div className="my-auto md:mr-6 ml-auto">
          <img className="record-card-right-img" src={lead} alt={`Lead Image`} />
        </div>
      </div>
    </>
  );
}
